import L from 'leaflet';
import Utils from '../../util/Utils';
import { createTooltip, TOOLTIP_MAX_LENGTH } from './MapManager';
import { getObjIdSearch, getIconByType } from '../../manager/SearchManager';
import { searchTypeMap } from '../../manager/searchConstants';
import {
    CATEGORY_TYPE,
    FINAL_POI_ICON_NAME,
    POI_ELO,
    RELATED_RESULT_IDS,
} from '../../infoblock/components/wpt/WptTagsProvider';
import PoiManager from '../../manager/PoiManager';
import { processMarkers } from '../layers/FavoriteLayer';
import { DEFAULT_ICON_SIZE } from '../markers/MarkerOptions';
import { getImgByProps, updateMarkerZIndex } from '../layers/ExploreLayer';
import { SimpleDotMarker } from '../markers/SimpleDotMarker';
import { MARKER_Z_INDEX_MAIN } from './ZIndexes';

export const EXPLORE_BIG_ICON_SIZE = 36;
export const EXPLORE_BIG_REAL_ICON_SIZE = 42;
export const SIMPLE_ICON_SIZE = 10;
const POI_MAIN_RADIUS_PX = 64; // gap between big markers
const POI_SECONDARY_RADIUS_PX = 12; // min gap between small dots

// Cluster markers based on zoom and coordinates
export function clusterMarkers({
    places,
    zoom,
    latitude,
    iconSize = EXPLORE_BIG_ICON_SIZE,
    secondaryIconSize = SIMPLE_ICON_SIZE,
    isPoi = false,
    isFavorites = false,
    isExplore = false,
    mainRadiusPx = POI_MAIN_RADIUS_PX,
    secondaryRadiusPx = POI_SECONDARY_RADIUS_PX,
}) {
    if (isPoi) {
        return createPoiMarkersArr({ places, latitude, zoom, mainRadiusPx, secondaryRadiusPx });
    }

    const maxMainPlaces = getMaxMainPlaces(isExplore);
    const maxSecondaryPlaces = getMaxSecondaryPlaces(zoom, isExplore);
    const useUniformMarkerPlacement = getUseUniformMarkerPlacement(zoom, isFavorites);

    // Minimum distances between markers in meters
    const mainMinDistance = calculateDistance({ iconSize, latitude, zoom });

    const secondaryMinDistance = calculateDistance({ iconSize: secondaryIconSize, latitude, zoom });

    if (isExplore) {
        return createExploreMarkersArr({
            places,
            mainMinDistance,
            secondaryMinDistance,
            maxMainPlaces,
            maxSecondaryPlaces,
        });
    }

    // Sort clusters by size
    const clusters = Object.values(clusterPlaces(places, zoom, isFavorites));

    if (isFavorites) {
        const res = createFavMarkersArr({
            clusters,
            mainMinDistance,
            secondaryMinDistance,
            isFavorites,
            zoom,
        });

        return {
            mainMarkers: res.mainMarkers,
            secondaryMarkers: res.secondaryMarkers,
        };
    }

    const mainMarkers = createMainMarkersArr(clusters, useUniformMarkerPlacement, mainMinDistance, isFavorites);

    const secondaryMarkers = createOtherMarkersArr({
        clusters,
        mainMarkers,
        maxMainPlaces,
        mainMinDistance,
        secondaryMinDistance,
        isFavorites,
    });

    return {
        mainMarkers: mainMarkers.slice(0, maxMainPlaces),
        secondaryMarkers: secondaryMarkers.slice(0, maxSecondaryPlaces),
    };
}

function createExploreMarkersArr({ places, mainMinDistance, secondaryMinDistance, maxMainPlaces, maxSecondaryPlaces }) {
    const sorted = [...places].sort((a, b) => (a.properties.rowNum ?? 0) - (b.properties.rowNum ?? 0));

    const mainMarkers = [];
    for (const place of sorted) {
        if (
            mainMarkers.length < maxMainPlaces &&
            place.properties.rowNum < 100 &&
            canPlaceMarker({
                place,
                existingPlaces: mainMarkers,
                minDistance: mainMinDistance,
                isFav: false,
            })
        ) {
            mainMarkers.push(place);
        }
    }

    const secondaryMarkers = [];
    for (const place of sorted) {
        if (secondaryMarkers.length >= maxSecondaryPlaces || mainMarkers.includes(place)) continue;

        if (
            canPlaceMarker({
                place,
                existingPlaces: [...mainMarkers, ...secondaryMarkers],
                minDistance: secondaryMinDistance,
                isFav: false,
            })
        ) {
            secondaryMarkers.push(place);
        }
    }

    return {
        mainMarkers,
        secondaryMarkers,
    };
}

// POI clustering by screen-pixel radius: big markers take the most popular (elo) place
// first, greedily kept mainRadiusPx apart; the rest become small dots kept secondaryRadiusPx
// apart. Both passes prevent overlap.
function createPoiMarkersArr({ places, latitude, zoom, mainRadiusPx, secondaryRadiusPx }) {
    const mpp = metersPerPixel(latitude, zoom);
    const mainMinDistance = mainRadiusPx * mpp;
    const valid = (places ?? []).filter(Boolean);

    const mainMarkers = [];
    for (const place of [...valid].sort((a, b) => getPoiElo(b) - getPoiElo(a))) {
        if (canPlaceMarker({ place, existingPlaces: mainMarkers, minDistance: mainMinDistance })) {
            mainMarkers.push(place);
        }
    }
    const mainSet = new Set(mainMarkers);

    const secondaryMinDistance = secondaryRadiusPx * mpp;
    const secondaryMarkers = [];
    const placed = [...mainMarkers];
    for (const place of valid) {
        if (mainSet.has(place)) continue;
        if (canPlaceMarker({ place, existingPlaces: placed, minDistance: secondaryMinDistance })) {
            placed.push(place);
            secondaryMarkers.push(place);
        }
    }
    return { mainMarkers, secondaryMarkers };
}

function getPoiElo(place) {
    return Number(place?.properties?.[POI_ELO]) || 0;
}

function getMaxMainPlaces(isExplore) {
    return isExplore ? 20 : 50;
}

function getMaxSecondaryPlaces(zoom, isExplore) {
    if (isExplore) {
        return 200;
    }
    if (zoom > 10) {
        return 200;
    }
    return 900;
}

function getUseUniformMarkerPlacement(zoom, isFavorites) {
    if (isFavorites) {
        return true;
    }
    return zoom <= 10 || zoom >= 16;
}

function calculateDistance({ iconSize, latitude, zoom }) {
    const baseDistance = iconSize * metersPerPixel(latitude, zoom);
    return zoom > 12 ? baseDistance * 1.5 : baseDistance * 2;
}

// Function to convert pixels to meters
const metersPerPixel = (latitude, zoomLevel) => {
    const earthCircumference = 40075017; // Earth's circumference in meters
    const latitudeRad = (latitude * Math.PI) / 180; // Convert latitude to radians
    const scale = Math.pow(2, zoomLevel); // Map scale depending on zoom level
    // 256 pixels per tile
    return (earthCircumference * Math.cos(latitudeRad)) / (256 * scale);
};

function getPointLatLon(point) {
    const lat = point.lat ?? point.ext?.lat;
    const lon = point.lon ?? point.ext?.lon;
    return lat != null && lon != null ? { lat: lat, lon: lon } : null;
}

// Function to check if a place can be added without overlapping
const canPlaceMarker = ({ place, existingPlaces, minDistance, isFav = false }) => {
    if (isFav) {
        const coords = getPointLatLon(place);
        const latlng = L.latLng(coords.lat, coords.lon);
        return existingPlaces.every((existingPlace) => {
            const coordsEx = getPointLatLon(existingPlace);
            const existingLatLng = L.latLng(coordsEx.lat, coordsEx.lon);
            return latlng.distanceTo(existingLatLng) >= minDistance;
        });
    } else {
        const latlng = L.latLng(place.geometry.coordinates[1], place.geometry.coordinates[0]);
        return existingPlaces.every((existingPlace) => {
            const existingLatLng = L.latLng(
                existingPlace.geometry.coordinates[1],
                existingPlace.geometry.coordinates[0]
            );
            return latlng.distanceTo(existingLatLng) >= minDistance;
        });
    }
};

function clusterPlaces(places, zoom, isFavorites) {
    const clustered = {};
    // the grid of the integer zoom level, the wheel zoom is fractional
    const shift = 31 - (Math.floor(zoom) + 1);

    for (const place of places) {
        if (!place) {
            continue;
        }
        const coords = isFavorites ? getPointLatLon(place) : null;
        const [lng, lat] = coords ? [coords.lon, coords.lat] : place.geometry.coordinates;
        const x31 = Math.floor(((lng + 180) / 360) * (1 << 31));
        const y31 = Math.floor(
            ((1 - Math.log(Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)) / Math.PI) / 2) *
                (1 << 31)
        );
        const key = ((x31 >> shift) & 0xffff) | (((y31 >> shift) & 0xffff) << 16);

        if (!clustered[key]) {
            clustered[key] = [];
        }
        clustered[key].push(place);
    }
    return clustered;
}

function createMainMarkersArr(clusters, useUniformMarkerPlacement, mainMinDistance, isFavorites) {
    const mainMarkers = [];
    if (useUniformMarkerPlacement) {
        if (!isFavorites) {
            // Remove images from clusters without icon
            clusters = clusters.map((cluster) => cluster.filter((item) => getImgByProps(item.properties)));
            // Remove empty clusters
            clusters = clusters.filter((cluster) => cluster.length > 0);
        }
        const firstItemsSorted = clusters.map((cluster) => cluster[0]).sort((a, b) => a.index - b.index);
        // Add first items to main markers for better visibility
        firstItemsSorted.forEach((item) => {
            if (
                canPlaceMarker({
                    place: item,
                    existingPlaces: mainMarkers,
                    minDistance: mainMinDistance,
                    isFav: isFavorites,
                })
            ) {
                mainMarkers.push(item);
            }
        });
    }
    return mainMarkers;
}

function createFavMarkersArr({ clusters, mainMinDistance, secondaryMinDistance, isFavorites, zoom }) {
    const mainMarkers = [];
    const secondaryMarkers = [];
    const MAIN_CLUSTER_SIZE = zoom < 10 ? 3 : 50;
    const OTHER_CLUSTER_SIZE = zoom < 10 ? 5 : 100;

    for (const cluster of clusters) {
        const mainClusterMarkers = [];
        const secondaryClusterMarkers = [];

        for (const place of cluster) {
            if (
                mainClusterMarkers.length < MAIN_CLUSTER_SIZE &&
                canPlaceMarker({
                    place,
                    existingPlaces: mainClusterMarkers,
                    minDistance: mainMinDistance,
                    isFav: isFavorites,
                })
            ) {
                mainClusterMarkers.push(place);
            } else if (
                secondaryClusterMarkers.length < OTHER_CLUSTER_SIZE &&
                canPlaceMarker({
                    place,
                    existingPlaces: [...mainClusterMarkers, ...secondaryClusterMarkers],
                    minDistance: secondaryMinDistance,
                    isFav: isFavorites,
                })
            ) {
                secondaryClusterMarkers.push(place);
            }

            if (
                mainClusterMarkers.length >= MAIN_CLUSTER_SIZE &&
                secondaryClusterMarkers.length >= OTHER_CLUSTER_SIZE
            ) {
                break;
            }
        }
        mainMarkers.push(...mainClusterMarkers);
        secondaryMarkers.push(...secondaryClusterMarkers);
    }
    return { mainMarkers, secondaryMarkers };
}

function createOtherMarkersArr({
    clusters,
    mainMarkers,
    maxMainPlaces,
    mainMinDistance,
    secondaryMinDistance,
    isFavorites,
}) {
    const secondaryMarkers = [];
    for (const cluster of clusters) {
        for (const place of cluster) {
            if (mainMarkers.includes(place)) {
                continue;
            }
            if (
                place.index < maxMainPlaces &&
                canPlaceMarker({ place, existingPlaces: mainMarkers, minDistance: mainMinDistance, isFav: isFavorites })
            ) {
                mainMarkers.push(place);
            } else if (
                canPlaceMarker({
                    place,
                    existingPlaces: [...mainMarkers, ...secondaryMarkers],
                    minDistance: secondaryMinDistance,
                    isFav: isFavorites,
                })
            ) {
                secondaryMarkers.push(place);
            }
        }
    }
    return secondaryMarkers;
}

export function createSecondaryMarker(obj) {
    if (!obj?.geometry || !obj?.properties) {
        return null;
    }
    const latlng = L.latLng(obj.geometry.coordinates[1], obj.geometry.coordinates[0]);

    let finalIconName = obj.properties[FINAL_POI_ICON_NAME];
    if (!finalIconName) {
        if (searchTypeMap.POI === obj.properties[CATEGORY_TYPE]) {
            finalIconName = PoiManager.getFinalPoiIconName(obj.properties);
        } else {
            finalIconName = getIconByType(obj.properties[CATEGORY_TYPE]);
        }
    }

    return new SimpleDotMarker(latlng, obj, {
        ...obj.properties,
        id: obj.properties.id,
        idObj: getObjIdSearch(obj),
        simple: true,
        [FINAL_POI_ICON_NAME]: finalIconName,
    }).build();
}

// The wheel zoom ends in zoomend on every notch: the hovered tooltip must survive it.
function getMarkerTooltipState(map) {
    if (map._markerTooltipState) return map._markerTooltipState;

    const state = (map._markerTooltipState = { zooming: false, hovered: null, frame: null });
    const cancelCheck = () => {
        L.Util.cancelAnimFrame(state.frame);
        state.frame = null;
    };
    map.on('zoomstart', () => {
        cancelCheck();
        state.zooming = true;
    });
    map.on('zoomend', () => {
        cancelCheck();
        // Keep hover locked until the browser has processed the final marker positions.
        state.frame = L.Util.requestAnimFrame(() => {
            state.frame = null;
            state.zooming = false;
            const hovered = state.hovered;
            if (hovered && !hovered.marker.getElement()?.matches(':hover')) {
                hovered.close();
            }
        });
    });
    map.once('unload', cancelCheck);
    return state;
}

export function addMarkerTooltip({
    marker,
    setSelectedId = null,
    mainStyle = false,
    text,
    latlng,
    iconSize = SIMPLE_ICON_SIZE,
    map,
    ctx,
    type = null,
}) {
    const tooltipRef = ctx.searchTooltipRef;
    const state = getMarkerTooltipState(map);

    function closeTooltip() {
        removeTooltip(map, tooltipRef);
        setSelectedId?.({ id: -1, show: false, type });
    }

    marker.on('mouseover', () => {
        // markers move under the cursor while zooming, those events are spurious
        if (state.zooming || state.hovered?.marker === marker) return;
        removeTooltip(map, tooltipRef);
        const relatedResultIds = (marker.options[RELATED_RESULT_IDS] ?? []).filter((id) => id != null);
        setSelectedId?.({
            id: marker.options.idObj,
            relatedResultIds,
            show: true,
            type,
            hoverFromMap: true,
            hoverLatlng: marker.getLatLng?.(),
        });
        if (text) {
            const offset = mainStyle ? [5, iconSize * 0.8] : [0, iconSize * 0.8];
            tooltipRef.current = createTooltip(Utils.truncateText(text, TOOLTIP_MAX_LENGTH), latlng, { offset });
            map.addLayer(tooltipRef.current);
        }
        state.hovered = { marker, tooltip: tooltipRef.current, close: closeTooltip };
    });

    marker.on('mouseout', (event) => {
        if (state.zooming || state.hovered?.marker !== marker) return;
        if (event.originalEvent) {
            if (!mainStyle && marker.options.selected) {
                return;
            }
            closeTooltip();
        }
    });
    // layers are rebuilt on moveend, the tooltip must not outlive its marker
    marker.on('remove', () => {
        if (!state.zooming && state.hovered?.marker === marker) {
            closeTooltip();
        }
    });
}

export function removeTooltip(map, tooltipRef) {
    const state = map._markerTooltipState;
    if (state && state.hovered?.tooltip === tooltipRef.current) {
        state.hovered = null;
    }
    if (tooltipRef.current && map.hasLayer(tooltipRef.current)) {
        map.removeLayer(tooltipRef.current);
        tooltipRef.current = null;
    }
}

export function addClusteredMarkersToMap({ map, markers, mainMarkers, secondaryMarkers, ctx, mapBounds, onClick }) {
    const markersToAdd = [];
    const mainLayers = [];
    const secondaryLayers = [];
    markers.eachLayer((layer) => {
        const markerLatLng = layer.getLatLng();
        if (!mapBounds.contains(markerLatLng)) {
            return;
        }
        if (!layer.options.hasClickHandler) {
            layer.on('click', onClick);
            layer.options.hasClickHandler = true;
        }
        processMarkers({
            layer,
            markerLatLng,
            mainMarkers,
            secondaryMarkers,
            mainLayers,
            secondaryLayers,
        });
    });
    markersToAdd.push(...mainLayers, ...secondaryLayers);
    markersToAdd.forEach((marker) => {
        addMarkerTooltip({
            marker,
            mainStyle: true,
            text: marker.options['name'] ?? marker.options['title'],
            latlng: marker._latlng,
            iconSize: DEFAULT_ICON_SIZE,
            map,
            ctx,
        });
    });
    const mainLayersGroup = new L.FeatureGroup(mainLayers);
    const secLayersGroup = new L.FeatureGroup(secondaryLayers);
    const res = new L.LayerGroup([secLayersGroup, mainLayersGroup]);
    res.addTo(map);
    updateMarkerZIndex(mainLayersGroup, MARKER_Z_INDEX_MAIN);

    return res;
}
